import React, { useEffect, useRef } from 'react';

/**
 * TAKIMYILDIZI AĞI — WhowSell Cloud sayfasının arka planı.
 *
 * Kaynak: radar-whowsell/components/cloud/constellation-grid.tsx
 *         (crm/ai/server kopyalarıyla mantık olarak BİREBİR aynı; aralarındaki
 *         tek fark nokta ve çizgi renkleri)
 *
 * Ne yapar: kutuyu kaplayan bir nokta ızgarası çizer, imleç yaklaştıkça
 * noktalar yay gibi kaçar ve yaylanarak yerine döner; yakın noktalar
 * birbirine ince çizgiyle bağlanır.
 *
 * SAPMALAR VE GEREKÇELERİ
 *  1. TypeScript -> saf JavaScript. Tip bildirimleri düştü, mantık aynı.
 *  2. Renkler: kaynakta radar'ın moru (216,195,255 / 91,33,182 / #a855f7)
 *     vardı. Burada bu sitenin marka rengi kullanıldı — App.css'teki
 *     --gradient-1 (#06b6d4) camgöbeği. Değerler server-whowsell'in aynı
 *     dosyasından alındı; o proje de camgöbeği ailesinde.
 *  3. Tema tespiti: kaynak `documentElement.classList.contains('dark')`
 *     bakıyor. Bu projede ThemeProvider hem `class` hem `data-theme`
 *     yazıyor; Tailwind burada class tabanlı olduğu için class izleniyor.
 */

const YAY = 18;        /* yay katsayısı: nokta yuvasına ne kadar sert döner */
const SONUM = 0.82;    /* sönümleme: 1'e yaklaştıkça daha uzun salınır */
const BAG = 64;        /* iki noktanın çizgiyle bağlanacağı en büyük uzaklık */

export default function ConstellationGrid() {
  const kutuRef = useRef(null);
  const tuvalRef = useRef(null);

  useEffect(() => {
    const kutu = kutuRef.current;
    const tuval = tuvalRef.current;
    if (!kutu || !tuval) return undefined;
    const ctx = tuval.getContext('2d', { alpha: true });
    if (!ctx) return undefined;

    const azHareket = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const kabaImlec = window.matchMedia('(pointer: coarse)').matches;
    const cekirdek = navigator.hardwareConcurrency || 4;

    let genislik = 0;
    let yukseklik = 0;
    let noktalar = [];
    let sutun = 0;
    let satir = 0;
    let kovalar = [];
    let kare = 0;
    let canli = true;
    let koyu = document.documentElement.classList.contains('dark');

    const imlec = { x: -9999, y: -9999, menzil: kabaImlec ? 130 : 190 };

    /* Zayıf cihazda ve dokunmatikte ızgara seyrekleşir: nokta sayısı
       aralığın karesiyle ters orantılı, 74px 56px'e göre yaklaşık yarı
       kadar nokta demek. */
    const aralik = () => {
      if (kabaImlec || cekirdek <= 4) return 74;
      if (genislik < 900) return 64;
      return 56;
    };

    const cozunurlukPayi = () => {
      const dpr = window.devicePixelRatio || 1;
      if (kabaImlec || cekirdek <= 4) return 1;
      return Math.min(dpr, 1.5);
    };

    const kur = () => {
      const kutuOlcu = kutu.getBoundingClientRect();
      genislik = Math.max(1, Math.round(kutuOlcu.width));
      yukseklik = Math.max(1, Math.round(kutuOlcu.height));
      const oran = cozunurlukPayi();
      tuval.width = Math.round(genislik * oran);
      tuval.height = Math.round(yukseklik * oran);
      tuval.style.width = `${genislik}px`;
      tuval.style.height = `${yukseklik}px`;
      ctx.setTransform(oran, 0, 0, oran, 0, 0);

      const adim = aralik();
      noktalar = [];
      for (let x = 0; x <= genislik + adim; x += adim) {
        for (let y = 0; y <= yukseklik + adim; y += adim) {
          noktalar.push({
            x,
            y,
            bx: x,
            by: y,
            vx: 0,
            vy: 0,
            r: Math.random() * 0.9 + 1,
            p: Math.random() * Math.PI * 2,
          });
        }
      }
      sutun = Math.ceil(genislik / BAG) + 3;
      satir = Math.ceil(yukseklik / BAG) + 3;
    };

    const ciz = () => {
      const dt = 0.016;
      ctx.clearRect(0, 0, genislik, yukseklik);

      for (let i = 0; i < noktalar.length; i += 1) {
        const n = noktalar[i];
        n.p += dt * 3;
        const dx = imlec.x - n.x;
        const dy = imlec.y - n.y;
        const uzaklik = Math.sqrt(dx * dx + dy * dy);
        if (uzaklik < imlec.menzil && uzaklik > 0) {
          const kuvvet = (1 - uzaklik / imlec.menzil) * 1250;
          const aci = Math.atan2(dy, dx);
          n.vx -= Math.cos(aci) * kuvvet * dt;
          n.vy -= Math.sin(aci) * kuvvet * dt;
        }
        n.vx += (n.bx - n.x) * YAY * dt;
        n.vy += (n.by - n.y) * YAY * dt;
        n.vx *= SONUM;
        n.vy *= SONUM;
        n.x += n.vx * dt * 60;
        n.y += n.vy * dt * 60;
      }

      /* Kovalama: her nokta BAG boyutunda bir hücreye düşer, komşuluk
         yalnızca kendi hücresi ve sağ/alt komşularında aranır. Böylece
         O(n²) karşılaştırma yerine sabit sayıda komşu taranır. */
      kovalar = new Array(sutun * satir);
      for (let i = 0; i < noktalar.length; i += 1) {
        const n = noktalar[i];
        const anahtar = (Math.floor(n.y / BAG) + 1) * sutun + Math.floor(n.x / BAG) + 1;
        const yuva = kovalar[anahtar];
        if (yuva) yuva.push(n);
        else kovalar[anahtar] = [n];
      }

      const cizgiTabani = koyu ? 0.16 : 0.1;
      ctx.lineWidth = 0.7;
      for (let r = 1; r < satir - 1; r += 1) {
        for (let c = 1; c < sutun - 1; c += 1) {
          const burada = kovalar[r * sutun + c];
          if (!burada) continue;
          for (let dr = 0; dr <= 1; dr += 1) {
            const dcBas = dr === 0 ? 0 : -1;
            for (let dc = dcBas; dc <= 1; dc += 1) {
              const yakin = kovalar[(r + dr) * sutun + (c + dc)];
              if (!yakin) continue;
              for (let a = 0; a < burada.length; a += 1) {
                for (let b = 0; b < yakin.length; b += 1) {
                  const na = burada[a];
                  const nb = yakin[b];
                  if (na === nb) continue;
                  const dx = na.x - nb.x;
                  const dy = na.y - nb.y;
                  const q = dx * dx + dy * dy;
                  if (q >= BAG * BAG) continue;
                  const saydam = (1 - Math.sqrt(q) / BAG) * cizgiTabani;
                  ctx.strokeStyle = koyu
                    ? `rgba(196, 224, 236, ${saydam.toFixed(3)})`
                    : `rgba(24, 62, 78, ${saydam.toFixed(3)})`;
                  ctx.beginPath();
                  ctx.moveTo(na.x, na.y);
                  ctx.lineTo(nb.x, nb.y);
                  ctx.stroke();
                }
              }
            }
          }
        }
      }

      for (let i = 0; i < noktalar.length; i += 1) {
        const n = noktalar[i];
        const dx = imlec.x - n.x;
        const dy = imlec.y - n.y;
        const yakin = dx * dx + dy * dy < imlec.menzil * imlec.menzil;
        const saydam = yakin ? 0.85 : (koyu ? 0.22 : 0.3) + Math.sin(n.p) * 0.07;
        ctx.fillStyle = yakin
          ? `rgba(6, 182, 212, ${saydam.toFixed(2)})`
          : koyu
            ? `rgba(196, 224, 236, ${saydam.toFixed(2)})`
            : `rgba(24, 62, 78, ${saydam.toFixed(2)})`;
        ctx.beginPath();
        ctx.arc(n.x, n.y, yakin ? n.r * 2 : n.r, 0, Math.PI * 2);
        ctx.fill();
      }

      if (canli) kare = requestAnimationFrame(ciz);
    };

    const basla = () => {
      if (kare || !canli) return;
      kare = requestAnimationFrame(ciz);
    };
    const dur = () => {
      if (!kare) return;
      cancelAnimationFrame(kare);
      kare = 0;
    };

    const imlecHareket = (olay) => {
      const olcu = kutu.getBoundingClientRect();
      imlec.x = olay.clientX - olcu.left;
      imlec.y = olay.clientY - olcu.top;
    };
    const dokunus = (olay) => {
      const parmak = olay.touches[0] || olay.changedTouches[0];
      if (!parmak) return;
      const olcu = kutu.getBoundingClientRect();
      imlec.x = parmak.clientX - olcu.left;
      imlec.y = parmak.clientY - olcu.top;
      if (!canli) return;
      if (!kare) basla();
    };
    const ayril = () => {
      imlec.x = -9999;
      imlec.y = -9999;
    };

    const boyutDegisti = () => { kur(); };
    const temaDegisti = () => { koyu = document.documentElement.classList.contains('dark'); };

    const gorunurluk = () => {
      canli = document.visibilityState === 'visible';
      if (canli) basla();
      else dur();
    };

    kur();

    if (azHareket) {
      ciz();
      canli = false;
      dur();
    } else {
      basla();
    }

    /* Bölüm ekrandan çıkınca animasyon durur: sayfanın altındaki altbilgide
       gezinirken boşuna kare üretilmez. */
    const gozlemci =
      typeof IntersectionObserver !== 'undefined'
        ? new IntersectionObserver(
            (girisler) => {
              for (const giris of girisler) {
                canli = giris.isIntersecting && document.visibilityState === 'visible';
                if (canli && !azHareket) basla();
                else dur();
              }
            },
            { rootMargin: '120px' },
          )
        : null;
    if (gozlemci) gozlemci.observe(kutu);

    const temaIzleyici = new MutationObserver(temaDegisti);
    temaIzleyici.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('pointermove', imlecHareket, { passive: true });
    window.addEventListener('touchmove', dokunus, { passive: true });
    window.addEventListener('touchstart', dokunus, { passive: true });
    window.addEventListener('touchend', ayril, { passive: true });
    window.addEventListener('touchcancel', ayril, { passive: true });
    window.addEventListener('pointerleave', ayril);
    window.addEventListener('resize', boyutDegisti);
    document.addEventListener('visibilitychange', gorunurluk);

    return () => {
      dur();
      canli = false;
      if (gozlemci) gozlemci.disconnect();
      temaIzleyici.disconnect();
      window.removeEventListener('pointermove', imlecHareket);
      window.removeEventListener('touchmove', dokunus);
      window.removeEventListener('touchstart', dokunus);
      window.removeEventListener('touchend', ayril);
      window.removeEventListener('touchcancel', ayril);
      window.removeEventListener('pointerleave', ayril);
      window.removeEventListener('resize', boyutDegisti);
      document.removeEventListener('visibilitychange', gorunurluk);
    };
  }, []);

  return (
    <div ref={kutuRef} className="wh-cloud-mesh" aria-hidden="true">
      <canvas ref={tuvalRef} />
    </div>
  );
}
